import React, { useState } from 'react';
import { closeAndReassign } from '../services/adminService';
import { monthBounds } from '../utils/period';

const BrickAssignmentPanel = ({
  repBricks, teams, regions, reps,
  tid, isSuperAdmin, mdTenantId,
  loadMasterData, setMessage, styles
}) => {
  const [filterTeamName, setFilterTeamName] = useState('');
  const [filterRegionName, setFilterRegionName] = useState('');
  const [filterRepName, setFilterRepName] = useState('');
  const [filterBrick, setFilterBrick] = useState('');
  const [filterType, setFilterType] = useState('');
  const [brickSort, setBrickSort] = useState({ col: 'brickName', dir: 'asc' });

  const [closingBrickId, setClosingBrickId] = useState(null);
  const [closingRb, setClosingRb] = useState(null);
  const [closingEndDate, setClosingEndDate] = useState('');
  const [closingMonth, setClosingMonth] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [saving, setSaving] = useState(false);

  const reload = () => loadMasterData(isSuperAdmin ? mdTenantId : null);

  const brickTypeInfo = (type) => {
    const t = (type || '').trim().toUpperCase();
    if (t === 'H') return { label: 'Hospital', color: '#0369a1', bg: '#e0f2fe' };
    if (t === 'R') return { label: 'Retail', color: '#9333ea', bg: '#f3e8ff' };
    return null;
  };

  const BrickTypeBadge = ({ type }) => {
    const info = brickTypeInfo(type);
    if (!info) return <span style={{ color: '#94a3b8' }}>-</span>;
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
        fontSize: '12px', fontWeight: 600, color: info.color, backgroundColor: info.bg
      }}>
        {info.label}
      </span>
    );
  };

  const visibleRegions = filterTeamName
    ? regions.filter(r => r.teamName === filterTeamName)
    : regions;

  const visibleReps = filterRegionName
    ? reps.filter(r => {
        const rg = regions.find(rg => rg.regionName === filterRegionName && rg.teamName === filterTeamName);
        return r.regionId === rg?.id;
      })
    : filterTeamName
      ? reps.filter(r => {
          const rg = regions.find(rg => rg.id === r.regionId);
          return rg?.teamName === filterTeamName;
        })
      : reps;

  const visibleBricks = repBricks
    .filter(rb => {
      if (!filterTeamName) return false;
      if (rb.teamName !== filterTeamName) return false;
      if (filterRegionName && rb.regionName !== filterRegionName) return false;
      if (filterRepName && rb.repName !== filterRepName) return false;
      if (filterBrick && !rb.brickName?.toLowerCase().includes(filterBrick.toLowerCase())) return false;
      if (filterType && (rb.type || '').trim().toUpperCase() !== filterType) return false;
      return true;
    })
    .sort((a, b) => {
      const dir = brickSort.dir === 'asc' ? 1 : -1;
      if (brickSort.col === 'brickName') return dir * (a.brickName || '').localeCompare(b.brickName || '', 'tr');
      if (brickSort.col === 'type') return dir * (a.type || '').localeCompare(b.type || '', 'tr');
      if (brickSort.col === 'teamName') return dir * (a.teamName || '').localeCompare(b.teamName || '', 'tr');
      if (brickSort.col === 'regionName') return dir * (a.regionName || '').localeCompare(b.regionName || '', 'tr');
      if (brickSort.col === 'repName') return dir * (a.repName || '').localeCompare(b.repName || '', 'tr');
      if (brickSort.col === 'startDate') return dir * (a.startDate || '').localeCompare(b.startDate || '');
      return 0;
    });

  const totalRatio = assignments.reduce((sum, a) => sum + (parseFloat(a.splitRatio) || 0), 0);

  // Yeniden atama yalnızca kapatılan satırın payı üzerinde yapılır; brick başka
  // mümessillerle paylaşılıyorsa (örn. 50/50) hedef toplam %100 değil, o satırın oranıdır.
  const capacity = parseFloat(closingRb?.splitRatio) || 100;
  const ratioOk = Math.abs(totalRatio - capacity) < 0.001;

  const handleMonthChange = (monthValue) => {
    setClosingMonth(monthValue);
    const { endDate, nextStart } = monthBounds(monthValue);
    setClosingEndDate(endDate);
    setNewStartDate(nextStart);
  };

  const addAssignmentRow = () => {
    setAssignments(prev => [...prev, {
      id: Date.now(),
      repId: '',
      splitRatio: capacity - totalRatio > 0 ? capacity - totalRatio : 0,
      whiteFlag: false,
      teamId: closingRb?.teamId || '',
      regionId: closingRb?.regionId || ''
    }]);
  };

  const updateAssignment = (id, field, value) => {
    setAssignments(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  const removeAssignment = (id) => {
    setAssignments(prev => prev.filter(a => a.id !== id));
  };

  const openClosingForm = (rb) => {
    setClosingBrickId(rb.id);
    setClosingRb(rb);
    setClosingMonth('');
    setClosingEndDate('');
    setNewStartDate('');
    setAssignments([{
      id: Date.now(),
      repId: '',
      splitRatio: rb.splitRatio,
      whiteFlag: false,
      teamId: rb.teamId || '',
      regionId: rb.regionId || ''
    }]);
  };

  const cancelClosing = () => {
    setClosingBrickId(null);
    setClosingRb(null);
    setClosingMonth('');
    setClosingEndDate('');
    setNewStartDate('');
    setAssignments([]);
  };

  const handleCloseAndReassign = async () => {
    if (!closingEndDate) { alert('Bitiş ayı seçin.'); return; }
    if (!newStartDate) { alert('Başlangıç tarihi hesaplanamadı.'); return; }
    if (assignments.length === 0) { alert('En az bir atama satırı ekleyin.'); return; }
    if (!ratioOk) { alert(`Toplam oran %${totalRatio} — %${capacity} olmalı.`); return; }
    for (const a of assignments) {
      if (!a.whiteFlag && !a.repId) { alert('Tüm satırlarda mümessil seçin veya White Flag işaretleyin.'); return; }
      if (a.whiteFlag && !a.regionId) { alert('White Flag satırlarında bölge seçin.'); return; }
    }
    setSaving(true);
    try {
      await closeAndReassign(closingRb.id, {
        endDate: closingEndDate,
        brickId: closingRb.brickId,
        newStartDate: newStartDate,
        assignments: assignments.map(a => ({
          repId: a.whiteFlag ? null : parseInt(a.repId),
          splitRatio: parseFloat(a.splitRatio),
          whiteFlag: a.whiteFlag,
          teamId: a.whiteFlag ? parseInt(a.teamId) : (() => {
            const rep = reps.find(r => r.id === parseInt(a.repId));
            const region = regions.find(r => r.id === rep?.regionId);
            return region?.teamId || null;
          })(),
          regionId: a.whiteFlag ? parseInt(a.regionId) : (() => {
            const rep = reps.find(r => r.id === parseInt(a.repId));
            return rep?.regionId || null;
          })()
        }))
      }, tid);
      cancelClosing();
      setMessage('Atama kapatıldı ve yeni atamalar oluşturuldu.');
      reload();
    } catch (err) {
      setMessage(err?.response?.data?.message || 'İşlem başarısız.');
    } finally { setSaving(false); }
  };

  const regionsByTeam = (teamId) => regions.filter(r => String(r.teamId) === String(teamId));

  const currentYear = new Date().getFullYear();
  const minMonth = closingRb?.startDate?.substring(0, 7);
  const maxMonth = `${currentYear + 1}-12`;

  const SortTh = ({ col, label }) => (
    <th
      style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => setBrickSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' })}
    >
      {label}{brickSort.col === col ? (brickSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
    </th>
  );

  return (
    <div style={styles.card}>
      <h3 style={styles.cardTitle}>📍 Brick Atamaları</h3>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={styles.label}>Ekip *</label>
          <select
            style={styles.input}
            value={filterTeamName}
            onChange={e => { setFilterTeamName(e.target.value); setFilterRegionName(''); setFilterRepName(''); setFilterBrick(''); setFilterType(''); }}
          >
            <option value="">Ekip seçin...</option>
            {teams.map(t => <option key={t.id} value={t.teamName}>{t.teamName}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={styles.label}>Bölge</label>
          <select
            style={{ ...styles.input, opacity: filterTeamName ? 1 : 0.5 }}
            value={filterRegionName}
            onChange={e => { setFilterRegionName(e.target.value); setFilterRepName(''); }}
            disabled={!filterTeamName}
          >
            <option value="">Tüm bölgeler</option>
            {visibleRegions.map(r => <option key={r.id} value={r.regionName}>{r.regionName}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={styles.label}>Mümessil</label>
          <select
            style={{ ...styles.input, opacity: filterTeamName ? 1 : 0.5 }}
            value={filterRepName}
            onChange={e => setFilterRepName(e.target.value)}
            disabled={!filterTeamName}
          >
            <option value="">Tüm mümessiller</option>
            {visibleReps.map(r => <option key={r.id} value={r.repName}>{r.repName}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={styles.label}>Brick</label>
          <input
            placeholder="Brick ara..."
            value={filterBrick}
            onChange={e => setFilterBrick(e.target.value)}
            style={{ ...styles.input, minWidth: '150px', opacity: filterTeamName ? 1 : 0.5 }}
            disabled={!filterTeamName}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={styles.label}>Tip</label>
          <select
            style={{ ...styles.input, opacity: filterTeamName ? 1 : 0.5 }}
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            disabled={!filterTeamName}
          >
            <option value="">Tüm tipler</option>
            <option value="H">Hospital</option>
            <option value="R">Retail</option>
          </select>
        </div>

        {filterTeamName && (
          <span style={{ fontSize: '13px', color: '#64748b', paddingBottom: '9px' }}>
            {visibleBricks.length} kayıt
          </span>
        )}
      </div>

      {!filterTeamName ? (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '32px' }}>
          Brick atamalarını görmek için ekip seçin.
        </p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <SortTh col="brickName" label="Brick" />
                  <SortTh col="type" label="Tip" />
                  <SortTh col="teamName" label="Ekip" />
                  <SortTh col="regionName" label="Bölge" />
                  <SortTh col="repName" label="Mümessil" />
                  <th style={styles.th}>Oran %</th>
                  <SortTh col="startDate" label="Başlangıç" />
                  <th style={styles.th}>Bitiş</th>
                  <th style={styles.th}>White</th>
                  <th style={styles.th}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {visibleBricks.map(rb => {
                  const isActive = !rb.endDate;
                  const isClosing = closingBrickId === rb.id;
                  return (
                    <tr key={rb.id} style={{ backgroundColor: isClosing ? '#fefce8' : 'transparent' }}>
                      <td style={styles.td}><strong>{rb.brickName}</strong></td>
                      <td style={styles.td}><BrickTypeBadge type={rb.type} /></td>
                      <td style={styles.td}><span style={styles.tenantBadge}>{rb.teamName || '-'}</span></td>
                      <td style={styles.td}>{rb.regionName || '-'}</td>
                      <td style={styles.td}>{rb.repName || <span style={{ color: '#94a3b8' }}>-</span>}</td>
                      <td style={styles.td}><span style={styles.badge}>{rb.splitRatio}%</span></td>
                      <td style={styles.td}>{rb.startDate}</td>
                      <td style={styles.td}>
                        {rb.endDate
                          ? <span style={{ color: '#ef4444' }}>{rb.endDate}</span>
                          : <span style={{ color: '#22c55e' }}>Aktif</span>}
                      </td>
                      <td style={styles.td}>
                        {rb.whiteFlag ? <span style={{ color: '#f59e0b' }}>⚑</span> : '-'}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {isActive && !isClosing && (
                            <button
                              style={{ padding: '4px 10px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                              onClick={() => openClosingForm(rb)}
                            >
                              Kapat
                            </button>
                          )}
                          {isClosing && (
                            <button style={styles.dangerButton} onClick={cancelClosing}>İptal</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {closingRb && (
            <div style={{ marginTop: '16px', padding: '20px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 4px 0', color: '#166534', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🔄 Yeniden Atama — <strong>{closingRb.brickName}</strong>
                <BrickTypeBadge type={closingRb.type} />
              </h4>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px 0' }}>
                Bitiş ayını seçin, yeni atama satırlarını ekleyin. Toplam oran, kapatılan atamanın oranı kadar (%{capacity}) olmalı. İkisi birlikte kaydedilecek.
              </p>
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ ...styles.label, color: '#dc2626', fontWeight: '600' }}>Mevcut Atama Bitiş Ayı *</label>
                  <input
                    type="month" value={closingMonth} min={minMonth} max={maxMonth}
                    onChange={e => handleMonthChange(e.target.value)}
                    style={{ ...styles.input, minWidth: '180px', borderColor: '#dc2626' }}
                  />
                  {closingEndDate && (
                    <span style={{ fontSize: '11px', color: '#dc2626' }}>Son gün: {closingEndDate}</span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ ...styles.label, color: '#166534', fontWeight: '600' }}>Yeni Atama Başlangıcı</label>
                  <div style={{ ...styles.input, minWidth: '180px', backgroundColor: '#e0f2fe', borderColor: '#166534', color: '#166534', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                    {newStartDate
                      ? new Date(newStartDate + 'T00:00:00').toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
                      : '— ay seçilince otomatik —'}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ ...styles.label, fontWeight: '600', fontSize: '13px' }}>
                    Yeni Atama Satırları
                    <span style={{ marginLeft: '8px', color: ratioOk ? '#166534' : '#dc2626', fontWeight: '700' }}>
                      (Toplam: %{totalRatio} / %{capacity})
                    </span>
                  </label>
                  <button style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px' }} onClick={addAssignmentRow}>
                    + Satır Ekle
                  </button>
                </div>

                {assignments.map((a, idx) => (
                  <div key={a.id} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', padding: '10px', marginBottom: '8px', backgroundColor: 'white', border: '1px solid #d1fae5', borderRadius: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b', alignSelf: 'center', minWidth: '20px' }}>{idx + 1}.</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <label style={styles.label}>Oran %</label>
                      <input
                        type="number" min="1" max={capacity} value={a.splitRatio}
                        onChange={e => updateAssignment(a.id, 'splitRatio', parseFloat(e.target.value) || 0)}
                        style={{ ...styles.input, minWidth: '70px', padding: '6px 8px' }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <label style={styles.label}>White Flag</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '36px' }}>
                        <input type="checkbox" checked={a.whiteFlag} onChange={e => updateAssignment(a.id, 'whiteFlag', e.target.checked)} />
                        <span style={{ fontSize: '13px' }}>Boş</span>
                      </div>
                    </div>
                    {a.whiteFlag ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={styles.label}>Bölge *</label>
                        <select
                          style={{ ...styles.input, minWidth: '180px', padding: '6px 8px' }}
                          value={a.regionId}
                          onChange={e => updateAssignment(a.id, 'regionId', e.target.value)}
                        >
                          <option value="">Bölge seçin...</option>
                          {regionsByTeam(closingRb.teamId).map(r => (
                            <option key={r.id} value={r.id}>{r.regionName}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={styles.label}>Mümessil *</label>
                        <select
                          style={{ ...styles.input, minWidth: '240px', padding: '6px 8px' }}
                          value={a.repId}
                          onChange={e => updateAssignment(a.id, 'repId', e.target.value)}
                        >
                          <option value="">Mümessil seçin...</option>
                          {reps
                            .filter(r => {
                              const rg = regions.find(rg => rg.id === r.regionId);
                              return String(rg?.teamId) === String(closingRb.teamId);
                            })
                            .map(r => {
                              const rg = regions.find(rg => rg.id === r.regionId);
                              return <option key={r.id} value={r.id}>{rg?.regionName} → {r.repName}</option>;
                            })}
                        </select>
                      </div>
                    )}
                    {assignments.length > 1 && (
                      <button style={{ ...styles.dangerButton, alignSelf: 'flex-end' }} onClick={() => removeAssignment(a.id)}>✕</button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  style={{ ...styles.primaryButton, opacity: (saving || !ratioOk) ? 0.6 : 1 }}
                  disabled={saving || !ratioOk}
                  onClick={handleCloseAndReassign}
                >
                  {saving ? 'Kaydediliyor...' : `💾 Kaydet (Toplam: %${totalRatio} / %${capacity})`}
                </button>
                <button style={styles.dangerButton} onClick={cancelClosing}>İptal</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrickAssignmentPanel;